import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  DeleteButton,
  ExportButton,
  FormButton,
  copy,
  deletionAlert,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../_helpers/Helpers";
import * as actions from "../../_redux/personnel-types/PersonnelTypesAction";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { PersonnelTypesFilter } from "./personnel-types-filter/PersonnelTypesFilter";
import { PersonnelTypesTable } from "./personnel-types-table/PersonnelTypesTable";
import { usePersonnelTypesUIContext } from "./PersonnelTypesUIContext";

export function PersonnelTypesCard() {
  // PersonnelTypes UI Context
  const personnelTypesUIContext = usePersonnelTypesUIContext();
  const personnelTypesUIProps = useMemo(() => {
    return {
      ids: personnelTypesUIContext.ids,
      setIds: personnelTypesUIContext.setIds,
      queryParams: personnelTypesUIContext.queryParams,
      openNewPersonnelTypePage: personnelTypesUIContext.openNewPersonnelTypePage,
    };
  }, [personnelTypesUIContext]);

  // Getting curret state of Personnel Type list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.personnelTypes }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isAdditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);
  
  // PersonnelTypes Redux state
  const dispatch = useDispatch();

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);
  
  /** Belli Bir Personel Türü Listesini Excele Aktarır */
  const exportPersonnelTypesToExcel = () => {
    dispatch(actions.getAllPersonnelTypes(personnelTypesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [30, 45];
      const columns = ["Adı", "Tanımı"];
      entities.forEach(entity => rows.push([entity.name, entity.definition]));
      const filename = "Personel Türü Listesi";
      exportToExcel({ columns, rows, widths, filename });
    });
  }

  /** Belli Bir Branş Listesini PDF e Aktarır */
  const exportPersonnelTypesToPDF = () => {
    dispatch(actions.getAllPersonnelTypes(personnelTypesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "Adı",
          rows: entities.map(entity => entity.name)
        },
        {
          header: "Tanımı",
          rows: entities.map(entity => entity.definition)
        }
      ];
  
      const filename = "Personel Türü Listesi";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Personel Türü Listesini Yazdırma Penceresini Açar */
  const printPersonnelTypes = () => {
    dispatch(actions.getAllPersonnelTypes(personnelTypesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = "Personel Türü Listesi";
      const properties = ["Adı", "Tanımı"];

      entities.forEach(entity => {
        data.push({
          "Adı": entity.name,
          "Tanımı": entity.definition
        });
      });

      print({ data, properties, header });
    });
  }

  /** Belli Bir Personel Türü Listesini Panoya Kopyalar */
  const copyPersonnelTypes = () => {
    dispatch(actions.getAllPersonnelTypes(personnelTypesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const header = "Personel Türü Listesi";
      const columns = ["Adı", "Tanımı"];
      entities.forEach(entity => rows.push([entity.name, entity.definition]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Personel Türü Listesi Siliniyor */
  const deletePersonnelTypes = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();        
      return;
    }  
    deletionAlert().then(result => {
      if (result.isConfirmed) dispatch(actions.deletePersonnelTypes(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
  
    if (isSuccess) {
      setTimeout(() => {
        // refresh list after deletion
        dispatch(actions.getPersonnelTypes(personnelTypesUIProps.queryParams));
        // clear selections list
        personnelTypesUIProps.setIds([]);
      }, 2500);
    }
  };
  
  const title = <>Personel Türü Listesi{personnelTypesUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{personnelTypesUIProps.ids.length}</span>]</> :
                   ""}</>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isAdditionAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportPersonnelTypesToExcel}
                exportToPDF={exportPersonnelTypesToPDF}
                print={printPersonnelTypes}
                copy={copyPersonnelTypes}
              />
            }
            {
              isAdditionAuthorized &&
              <FormButton
                path="Navigation/Plus"
                title="Yeni Personel Türü"
                method={personnelTypesUIProps.openNewPersonnelTypePage}
              />
            }              
            {
              isDeletionAuthorized && 
              <DeleteButton 
                title="Seçilenleri Sil"
                method={() => deletePersonnelTypes(personnelTypesUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <PersonnelTypesFilter />
        <PersonnelTypesTable
          deletePersonnelTypes={deletePersonnelTypes}
        />
      </CardBody>
    </Card>
  );
}